import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { AI_ACKNOWLEDGEMENT } from '../../legal/legalContent';

const COINS_PER_MINUTE = 10;

const SessionReadiness = ({ config, onStart, loading, isPlanSession = false, isChallenge = false }) => {
    const [aiAcknowledgementAccepted, setAiAcknowledgementAccepted] = React.useState(false);
    // Pilot disabled: Focus Topics are hidden during restricted pilot.
    // const topics = config?.advancedSettings?.topics || [];
    const duration = Number(config?.duration) || 0;
    const formatInterviewType = (type) => {
        if (!type) return 'Required';
        return type === 'CODING' ? 'DSA' : type.replace(/_/g, ' ');
    };
    /*
    Pilot disabled: restore when Focus Topics returns.
    const formatTopics = () => {
        if (topics.length === 0) return 'Optional';
        const preview = topics.slice(0, 2).join(', ');
        return topics.length > 2 ? `${preview} +${topics.length - 2}` : preview;
    };
    */
    const readinessItems = [
        { label: 'Interview Type', value: formatInterviewType(config?.interviewType), muted: !config?.interviewType },
        { label: 'Duration', value: duration ? `${duration}m` : 'Required', muted: !duration },
        /*
        Pilot disabled: Focus Topics row hidden during restricted pilot.
        { label: 'Technical Topics', value: formatTopics(), muted: topics.length === 0 },
        */
    ];

    const isReady = Boolean(config?.interviewType);
    const canStart = isReady && aiAcknowledgementAccepted && !loading;
    const coinsToDeduct = duration * COINS_PER_MINUTE;
    const coinMessage = isPlanSession
        ? 'No extra coins will be deducted for this structured plan session.'
        : isChallenge
            ? 'No coins will be deducted for this challenge entry.'
            : `${coinsToDeduct} coins will be deducted when you initialize this ${duration}-minute interview (${COINS_PER_MINUTE} coins/minute).`;
    const refundMessage = isPlanSession || isChallenge
        ? 'Coin-based sessions refund unused coins when they end before the expected duration.'
        : 'If the interview ends before the expected duration, unused coins are refunded based on unused minutes.';

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-8">
                <h3 className="text-[10px] font-normal tracking-widest text-slate-900 mb-6 flex items-center gap-2">
                    <Icon name="CheckCircle" size={12} className="text-emerald-500" /> Session Readiness
                </h3>
                
                <div className="space-y-4 mb-8">
                    {readinessItems.map((item) => (
                        <div key={item.label} className="flex items-center justify-between gap-4">
                            <span className="text-[12px] font-normal text-slate-800">{item.label}</span>
                            <span className={`max-w-[9rem] truncate rounded-lg px-2.5 py-1 text-right text-[10px] font-normal ${
                                item.muted
                                    ? 'bg-slate-50 text-slate-400'
                                    : 'bg-emerald-50 text-emerald-700'
                            }`}>
                                {item.value}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 mb-8">
                    <div className="flex items-start gap-3">
                        <Icon name="Info" size={16} className="text-slate-600 mt-0.5" />
                        <p className="text-[11px] text-slate-700 leading-relaxed font-normal">
                            {coinMessage} {refundMessage} Your AI interviewer will be prepared based on these parameters.
                        </p>
                    </div>
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-emerald-100 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/10 p-4 mb-8">
                    <input
                        type="checkbox"
                        checked={aiAcknowledgementAccepted}
                        onChange={(e) => setAiAcknowledgementAccepted(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[11px] text-slate-700 leading-relaxed font-normal">
                        {AI_ACKNOWLEDGEMENT}{' '}
                        <Link to="/interview-consent" className="font-normal text-emerald-700 hover:underline">
                            View consent
                        </Link>
                    </span>
                </label>

                <button
                    disabled={!canStart}
                    onClick={onStart}
                    className={`w-full py-4 rounded-xl text-[12px] font-normal tracking-widest transition-all duration-300 flex items-center justify-center gap-3 ${
                        canStart
                            ? 'bg-slate-900 text-white hover:bg-slate-800 active:scale-[0.98]'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                >
                    {loading ? (
                        <>
                            <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            Preparing Session...
                        </>
                    ) : (
                        <>
                            <Icon name="Play" size={14} className="fill-current" />
                            Initialize Interview
                        </>
                    )}
                </button>
                
                {!isReady && (
                    <p className="text-center text-[10px] font-normal text-red-400 mt-4">
                        Complete configuration to begin
                    </p>
                )}
                {isReady && !aiAcknowledgementAccepted && (
                    <p className="text-center text-[10px] font-normal text-amber-500 mt-4">
                        Confirm AI acknowledgement to begin
                    </p>
                )}
            </div>
        </div>
    );
};

export default SessionReadiness;
